/**
 * Update POS Health Actions
 *
 * Synchronous actions for updating POS health status in context.
 * Used by the posHealth parallel state to track reader status.
 */

import { datadogLogs } from "@datadog/browser-logs";
import { assign } from "xstate";
import type { ActionWithMetadata } from "./save-error.action";

/**
 * Set POS Health to Healthy
 *
 * Updates context when POS reader reports healthy status.
 */
export const setPOSHealthy: ActionWithMetadata = {
  impl: assign(
    ({ event }: { event: { lastSeen?: Date; readerId?: string } }) => {
      const lastSeen =
        event.lastSeen?.toISOString() || new Date().toISOString();

      console.log("[setPOSHealthy] Reader is healthy", {
        lastSeen,
        readerId: event.readerId,
      });

      return {
        posHealthStatus: "healthy" as const,
        posLastChecked: new Date().toISOString(),
        posLastSeen: lastSeen,
        posHealthError: undefined,
        posHealthDegradeReason: undefined,
      };
    },
  ),
  metadata: {
    displayName: "setPOSHealthy",
    description: "Set POS health status to healthy",
    category: "context",
  },
};

/**
 * Set POS Health to Degraded
 *
 * Updates context when POS reader reports degraded status.
 * Includes reason for degradation (e.g., stale last_seen).
 */
export const setPOSDegraded: ActionWithMetadata = {
  impl: assign(
    ({ event }: { event: { reason?: string; readerId?: string } }) => {
      const reason = event.reason || "Unknown degradation";

      console.warn("[setPOSDegraded] Reader is degraded", {
        reason,
        readerId: event.readerId,
      });

      datadogLogs.logger.warn("[POSHealth] Reader degraded", {
        reason,
        readerId: event.readerId,
      });

      return {
        posHealthStatus: "degraded" as const,
        posLastChecked: new Date().toISOString(),
        posHealthDegradeReason: reason,
        posHealthError: undefined,
      };
    },
  ),
  metadata: {
    displayName: "setPOSDegraded",
    description: "Set POS health status to degraded with reason",
    category: "context",
  },
};

/**
 * Set POS Health to Offline
 *
 * Updates context when POS reader reports offline status.
 */
export const setPOSOffline: ActionWithMetadata = {
  impl: assign(
    ({ event }: { event: { reason?: string; readerId?: string } }) => {
      const reason = event.reason || "Reader offline";

      console.error("[setPOSOffline] Reader is offline", {
        reason,
        readerId: event.readerId,
      });

      datadogLogs.logger.error("[POSHealth] Reader offline", {
        reason,
        readerId: event.readerId,
      });

      return {
        posHealthStatus: "offline" as const,
        posLastChecked: new Date().toISOString(),
        posHealthError: reason,
        posHealthDegradeReason: undefined,
      };
    },
  ),
  metadata: {
    displayName: "setPOSOffline",
    description: "Set POS health status to offline with error",
    category: "context",
  },
};

/**
 * Set POS Health to Unknown (No Reader)
 *
 * Updates context when no POS reader is configured.
 */
export const setPOSNoReader: ActionWithMetadata = {
  impl: assign(() => {
    console.log("[setPOSNoReader] No reader configured");

    return {
      posHealthStatus: "unknown" as const,
      posLastChecked: new Date().toISOString(),
      posHealthError: "No reader configured",
      posHealthDegradeReason: undefined,
      posLastSeen: undefined,
    };
  }),
  metadata: {
    displayName: "setPOSNoReader",
    description: "Set POS health status to unknown (no reader configured)",
    category: "context",
  },
};

/**
 * Clear POS Health Status
 *
 * Resets all POS health fields in context.
 * Used when restarting the flow or changing reader configuration.
 */
export const clearPOSHealth: ActionWithMetadata = {
  impl: assign(() => {
    console.log("[clearPOSHealth] Clearing POS health status");

    return {
      posHealthStatus: undefined,
      posLastChecked: undefined,
      posLastSeen: undefined,
      posHealthError: undefined,
      posHealthDegradeReason: undefined,
    };
  }),
  metadata: {
    displayName: "clearPOSHealth",
    description: "Clear all POS health status fields",
    category: "context",
  },
};

/**
 * Log POS Health Warning
 *
 * Logs a warning about POS health degradation to console and Datadog.
 * Used before payment flow to alert about potential issues.
 */
export const logPOSHealthWarning: ActionWithMetadata = {
  impl: assign(({ context }) => {
    const status = context.posHealthStatus;
    const reason = context.posHealthDegradeReason || context.posHealthError;

    if (status === "degraded" || status === "offline") {
      console.warn("[logPOSHealthWarning] POS health warning before payment", {
        status,
        reason,
        readerId: context.stripeReaderId,
      });

      datadogLogs.logger.warn("[POSHealth] Health warning before payment", {
        status,
        reason,
        readerId: context.stripeReaderId,
        practiceId: context.practiceId,
      });
    }

    // Action doesn't modify context, just logs
    return {};
  }),
  metadata: {
    displayName: "logPOSHealthWarning",
    description: "Log warning about POS health status before payment",
    category: "logging",
  },
};
